import React, { useEffect, lazy } from 'react';
import { X, Check } from 'lucide-react';
import { trackCheckoutClick } from '../utils/metaPixel';
// Shopify Cart Permalinks only — no /cart/add.js, no AJAX, no APIs.
const MAIN_PRODUCT_VARIANT_ID = '44687883272278';
const UPSELL_PRODUCT_VARIANT_ID = '44802602434646';
const SHOPIFY_CART_DOMAIN = 'https://impulsodigital.vip/cart/';
const CHECKOUT_MAIN_ONLY = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1`;
const CHECKOUT_WITH_UPSELL = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1,${UPSELL_PRODUCT_VARIANT_ID}:1`;
const UPSELL_COVER_IMG = 'https://cdn.magicpatterns.com/uploads/dBGdH38qme2nPzThoCzoCq/postres_bono_50kb.webp';
function goToMainOnlyCheckout() {
  trackCheckoutClick();
  window.location.href = CHECKOUT_MAIN_ONLY;
}
function goToUpsellCheckout() {
  trackCheckoutClick();
  window.location.href = CHECKOUT_WITH_UPSELL;
}
type UpsellModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
export function UpsellModal({
  isOpen,
  onClose
}: UpsellModalProps) {
  // Lock body scroll while open + close on Esc
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const benefits = ['+120 postres fáciles y rápidos', 'Ideales para meriendas, antojos y reuniones', 'Hechos en air fryer — sin usar horno', 'Recibís todo junto en formato digital'];
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="upsell-title" data-id="element-166">
      <div className="relative bg-white rounded-3xl w-full max-w-md my-auto shadow-2xl border border-[#F3E9DC]" onClick={e => e.stopPropagation()} data-id="element-167">
        {/* Close button */}
        <button type="button" onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors shadow-sm" data-id="element-168">
          <X size={16} data-id="element-169" />
        </button>

        <div className="px-5 pt-6 pb-6 sm:px-7 sm:pt-7 sm:pb-7" data-id="element-170">
          {/* Badge */}
          <div className="flex justify-center mb-3" data-id="element-171">
            <span className="inline-block bg-[#FFF4E5] text-[#C2410C] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-[#FBD9B0]" data-id="element-172">
              Oferta por única vez
            </span>
          </div>

          {/* Title */}
          <h2 id="upsell-title" className="font-serif text-xl sm:text-2xl font-semibold text-center text-[#111111] leading-tight mb-4 tracking-tight" data-id="element-173">
            Antes de finalizar tu compra…
          </h2>

          {/* Product row: cover + name */}
          <div className="flex items-center gap-4 mb-4 p-3 rounded-2xl bg-[#FAF7F2] border border-[#F3E9DC]" data-id="element-174">
            <img src={UPSELL_COVER_IMG} alt="Pack de Postres y Repostería para Air Fryer" loading="lazy" decoding="async" className="w-20 sm:w-24 flex-shrink-0 rounded-lg shadow-sm object-cover" data-id="element-175" />
            <div className="min-w-0" data-id="element-176">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#C2410C] mb-1" data-id="element-177">
                Sumá a tu pedido
              </p>
              <p className="font-serif text-base sm:text-lg font-semibold text-[#111111] leading-snug" data-id="element-178">
                +120 Postres Irresistibles para Air Fryer
              </p>
            </div>
          </div>

          {/* Price block */}
          <div className="text-center mb-4" data-id="element-179">
            <div className="flex items-baseline justify-center gap-2 mb-0.5 whitespace-nowrap" data-id="element-180">
              <span className="text-gray-400 line-through text-sm" data-id="element-181">
                $12.000
              </span>
              <span className="text-2xl sm:text-3xl font-bold text-[#C2410C]" data-id="element-182">
                $3.990
              </span>
            </div>
            <p className="text-xs text-gray-500" data-id="element-183">
              Tu compra queda en{' '}
              <span className="font-semibold text-[#4b4b4b]" data-id="element-184">
                $16.980 total
              </span>
            </p>
          </div>

          {/* Benefits — compact */}
          <ul className="space-y-1.5 mb-5" data-id="element-185">
            {benefits.map((benefit, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#1a1a1a]" data-id="element-186">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-[#16a34a]/10 text-[#16a34a] flex items-center justify-center mt-0.5" data-id="element-187">
                  <Check size={10} strokeWidth={3} data-id="element-188" />
                </span>
                <span className="leading-snug" data-id="element-189">{benefit}</span>
              </li>)}
          </ul>

          {/* Primary CTA */}
          <button type="button" onClick={goToUpsellCheckout} className="w-full bg-[#16a34a] hover:bg-[#15803d] active:bg-[#14532d] text-white font-bold text-base py-3.5 px-6 rounded-2xl transition-colors shadow-md mb-2" data-id="element-190">
            Sí, agregar por solo $3.990
          </button>

          {/* Secondary CTA — discreet but tappable */}
          <button type="button" onClick={goToMainOnlyCheckout} className="w-full text-sm sm:text-base text-gray-600 hover:text-gray-800 font-medium border border-gray-200 hover:border-gray-300 rounded-2xl transition-colors py-3" data-id="element-191">
            No gracias, continuar solo con mis recetas
          </button>

          {/* Trust line */}
          <p className="text-[10px] sm:text-[11px] text-gray-400 text-center mt-2 leading-snug" data-id="element-192">
            Se agrega automáticamente y recibís todo junto en formato digital.
          </p>
        </div>
      </div>
    </div>;
}